
import React from "react";
import { formatText } from "@/utils/report";

type BulletedListProps = {
  items?: string[];
  content?: string;
  className?: string;
  bulletColor?: string;
};

/**
 * A flexible bullet list component that can render from either:
 * - An array of items
 * - Raw content text that contains bullet points
 */
const BulletedList: React.FC<BulletedListProps> = ({ 
  items = [], 
  content = "", 
  className = "",
  bulletColor = "text-blue-400" 
}) => {
  // Process items from content if provided and items array is empty
  const listItems = items.length > 0 
    ? items 
    : content
        .split('\n')
        .map(line => line.trim())
        .filter(line => line.startsWith('-') || line.startsWith('•') || line.startsWith('*'))
        .map(line => line.replace(/^[-•*]\s+/, '').trim());
  
  // If no items, return nothing
  if (listItems.length === 0) {
    return null;
  }
  
  return (
    <ul className={`list-none space-y-2 text-gray-300 mb-4 ${className}`}>
      {listItems.map((item, i) => {
        // Check if this is a placeholder or error message
        const isPlaceholder = item.includes("XYZ") || item.includes("ABC");
        const isError = item.toLowerCase().includes("error") || 
                        item.toLowerCase().includes("failed") ||
                        item.toLowerCase().includes("apologize");
        
        // Clean up the item text - remove formatting markers
        const cleanedItem = formatText(item)
          .replace(/\*\*/g, '') // Remove ** markup
          .replace(/\*/g, '')   // Remove * markup
          .replace(/#/g, '')    // Remove # characters
          .replace(/\[([^\]]+)\]/g, "$1") // Remove brackets
          .replace(/(\d+)\s+percent/gi, "$1%"); // Convert "XX percent" to "XX%"
        
        // Special rendering for placeholder content
        if (isPlaceholder) {
          return (
            <li key={i} className="text-base leading-relaxed text-gray-400 italic flex items-start py-1">
              <span className={`${bulletColor} mr-2.5 inline-block mt-1 flex-shrink-0`}>•</span>
              <span className="inline-block bg-gray-800/20 px-3 py-1 rounded">
                N/A - Specific information not available
              </span>
            </li>
          );
        }
        
        // Standard bullet point styling
        return (
          <li key={i} className={`text-base leading-relaxed flex items-start py-1 ${isError ? "text-red-400" : ""}`}>
            <span className={`${isError ? "text-red-400" : bulletColor} mr-2.5 inline-block mt-1 flex-shrink-0`}>•</span>
            <span 
              className="inline-block"
              dangerouslySetInnerHTML={{
                __html: cleanedItem,
              }}
            />
          </li>
        );
      })}
    </ul>
  );
};

export default BulletedList;
